import { useState, useRef, useEffect, useCallback } from 'react';

export const LEASE_ABSTRACTION_SPLIT_STORAGE_KEY_PREFIX = 'la_split_';

export const getLeaseAbstractionSplitStorageKey = dealId => LEASE_ABSTRACTION_SPLIT_STORAGE_KEY_PREFIX + dealId;

export const getSplitPaneWidthCssValue = width => String(width) + '%';
export const DEFAULT_SPLIT_PANE_DIVIDER_HINT_TEXT = 'Drag to resize';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const useResizableSplitPane = ({
  defaultLeftWidth = 75,
  minLeftWidth = 30,
  maxLeftWidth = 75,
  storageKey,
  dividerHintText = DEFAULT_SPLIT_PANE_DIVIDER_HINT_TEXT,
} = {}) => {
  const [leftPaneWidth, setLeftPaneWidth] = useState(defaultLeftWidth);
  const containerRef = useRef(null);
  const isResizingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) return;

    try {
      const storedValue = window.localStorage.getItem(storageKey);
      if (storedValue == null) return;

      const parsedValue = Number(storedValue);
      if (Number.isNaN(parsedValue)) return;

      setLeftPaneWidth(clamp(parsedValue, minLeftWidth, maxLeftWidth));
    } catch (_error) {
      // Ignore storage access errors and continue with default width.
    }
  }, [maxLeftWidth, minLeftWidth, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) return;

    try {
      window.localStorage.setItem(storageKey, String(leftPaneWidth));
    } catch (_error) {
      // Ignore storage access errors to avoid breaking interaction.
    }
  }, [leftPaneWidth, storageKey]);

  const updateWidthFromClientX = useCallback((clientX) => {
    if (!isResizingRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    if (!rect.width) return;

    const leftPercentage = ((clientX - rect.left) / rect.width) * 100;
    setLeftPaneWidth(clamp(leftPercentage, minLeftWidth, maxLeftWidth));
  }, [maxLeftWidth, minLeftWidth]);

  const stopResize = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    document.body.classList.remove('is-resizing-abstraction-pane');
  }, []);

  const handleMouseMove = useCallback((event) => {
    updateWidthFromClientX(event.clientX);
  }, [updateWidthFromClientX]);

  const handleTouchMove = useCallback((event) => {
    if (!event.touches || event.touches.length === 0) return;
    updateWidthFromClientX(event.touches[0].clientX);
    event.preventDefault();
  }, [updateWidthFromClientX]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResize);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', stopResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResize);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopResize);
      document.body.classList.remove('is-resizing-abstraction-pane');
    };
  }, [handleMouseMove, handleTouchMove, stopResize]);

  const startResize = useCallback((event) => {
    if (typeof event.button === 'number' && event.button !== 0) return;
    isResizingRef.current = true;
    document.body.classList.add('is-resizing-abstraction-pane');
    event.preventDefault();
  }, []);

  const handleDividerKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      setLeftPaneWidth(prev => clamp(prev - 2, minLeftWidth, maxLeftWidth));
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      setLeftPaneWidth(prev => clamp(prev + 2, minLeftWidth, maxLeftWidth));
      event.preventDefault();
    } else if (event.key === 'Home') {
      setLeftPaneWidth(minLeftWidth);
      event.preventDefault();
    } else if (event.key === 'End') {
      setLeftPaneWidth(maxLeftWidth);
      event.preventDefault();
    }
  }, [maxLeftWidth, minLeftWidth]);

  return {
    containerRef,
    leftPaneWidth,
    minLeftWidth,
    maxLeftWidth,
    startResize,
    handleDividerKeyDown,
    dividerHintText,
  };
};

export default useResizableSplitPane;
